import { cva } from '../../shared';

// Every value below resolves through this design system's existing tokens (control-height/
// caption/body text sizes, border/surface colors) — none of the three density levels or the
// grid/list variant introduces a new grid-specific token.

// The last body row's cells drop their own bottom border: `<DataGrid>`'s outer frame (a `border`
// wrapping the table, see components/data-grid.tsx) already closes the grid off at that exact
// edge, flush against it — without this, that frame's 1px border and the last row's own 1px
// `border-b` (cellVariants) sit immediately adjacent and read as one noticeably thicker line,
// inconsistent with every other row divider in the grid, which is a single 1px line.
// `table-fixed`: under the browser default `table-layout: auto`, an explicit per-cell `width`
// (set in `data-grid-cell.tsx`/`data-grid-header-cell.tsx` from `distribute.ts`'s own computed
// widths) is only a hint — a column whose content is one long unbroken run can still force the
// browser to widen that column, and the table with it, defeating the readable-minimum invariant
// this sizing system exists to enforce. `table-layout: fixed` makes those widths authoritative, so
// overflow is truncated by `cellContentVariants` instead of expanding the table.
export const tableVariants = cva(
  'w-full table-fixed border-collapse [&_tbody>tr:last-child>td]:border-b-0',
);

// No border classes here, deliberately: `tableVariants` sets `border-collapse: collapse`, and
// under the CSS collapsed-borders model a `<tr>`'s own border is never painted — only `<td>`/`<th>`
// cell borders participate. A `border-b` here would compute a real value (`getComputedStyle` even
// reports it) while the browser silently discards it at paint time. Row/cell dividers live on
// `headerCellVariants`/`cellVariants` instead, which are cells and do participate.
//
// Selected replaces the row's hover/stripe treatment rather than layering under it: CVA doesn't
// resolve utility-class conflicts by string order (unlike `cn()`), so two background utilities
// present at once would race the generated stylesheet's own ordering. Never emitting both avoids
// that race entirely, and gives selection the unambiguous top slot in the row→stripe→hover→selected
// ladder a data grid needs.
// No `variant` (grid/list) axis here: `<tr>`-level borders are the very thing this file's own
// comment above rules out, and background is the only other thing a row contributes — which is
// identical in both variants. `variant` still reaches `cellVariants`, where the border actually
// lives.
// A plain `outline`, not this design system's shared `focus-ring` utility: `focus-ring` composes
// its ring from `box-shadow`, and `box-shadow` on a `display: table-row` box (what a bare `<tr>`
// is) does not reliably paint in any browser tested — confirmed empirically (computed style shows
// a real value; nothing renders on screen), not assumed. `outline` has no such table-layout
// restriction. `-outline-offset-2` draws it *inside* the row's own edge rather than overlapping the
// row above/below or getting clipped by `<DataGrid>`'s `overflow-hidden` frame.
export const rowVariants = cva(
  // Neither a bare `outline-none` nor a `focus:outline-none` works here — confirmed by inspecting
  // the actual compiled CSS, not assumed: Tailwind v4's `outline`/`outline-<n>` utilities all read
  // `outline-style: var(--tw-outline-style)`, and *any* rule that matches while the row is
  // focus-visible and sets that variable to `none` poisons it for `focus-visible:outline` too,
  // since a custom property's last-matching assignment wins independent of which rule's own
  // `outline-style` declaration "wins" the cascade slot. `:focus` is not narrow enough — it's a
  // superset of `:focus-visible` (every focus-visible element is also just plain focused), so
  // `focus:outline-none` matches, and poisons the variable, at exactly the same time
  // `focus-visible:outline` does. `:focus:not(:focus-visible)` is the one selector that is
  // *mutually exclusive* with `:focus-visible` by construction — it can never poison the variable
  // in the one state that needs it.
  // `group` is load-bearing, not decoration: a pinned cell paints its own opaque background, so
  // the row's hover never reaches it and the row splits visually under the cursor. The pinned
  // cells mirror the hover themselves, and this is what they hook onto.
  'group [&:focus:not(:focus-visible)]:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring',
  {
    variants: {
      striped: {
        true: '',
        false: '',
      },
      selected: {
        true: 'bg-primary-subtle',
        false: '',
      },
    },
    compoundVariants: [
      { striped: true, selected: false, class: 'bg-muted hover:bg-accent' },
      { striped: false, selected: false, class: 'hover:bg-accent' },
    ],
    defaultVariants: { striped: false, selected: false },
  },
);

// `cozy`/`comfortable` share the same text size — only the row height they drive differs.
//
// The header's ground is `--surface-header`, a role token of its own rather than a borrowed one,
// and the numbers are why. `bg-secondary-subtle` (this file's first cut) is 1.09:1 against
// `--card` — indistinguishable from the surface it exists to separate from. `bg-secondary` (the
// cut after that) is 1.46:1, heavier than a header needs, and it is the same lightness as
// `--muted`, which `groupRowVariants` below already uses for group bands — so the header and a
// band read as one tone. `--surface-header` is 1.22:1: the separation `--accent` has from the
// page, which is this system's own standard for "one step off".
//
// `text-foreground` on it measures 14.98:1, far past the 4.5:1 AA floor for this text's actual
// size (`text-caption`, well under the "large text" threshold that would drop it to 3:1).
// `text-muted-foreground` is forbidden here and the trap is worse than it was: it reaches only
// 3.61:1 on this surface, against ~4.1:1 on the old one. Both fail AA; this one fails it further.
//
// The hover has its own trap — a base and a hover that are nearly the same lightness is not a
// hover at all. `--surface-header-hover` is the paired token, 1.20:1 from the base, matching what
// `hover:bg-accent` does for a row, with `text-foreground` still 12.47:1 on top of it.
export const headerCellVariants = cva(
  // `relative` so the resize handle and, later, the revealed controls anchor to the cell rather
  // than to the table. A pinned cell is `sticky`, which already establishes containment; this is
  // what gives every other cell the same.
  // `data-drop-target` is written straight onto the element by the reorder drag, never through
  // state — a drop indicator that re-rendered the header every frame would be the one thing the
  // gesture is built to avoid. Inset shadow rather than a border, for the reason pinned cells use
  // one: `border-collapse` does not reliably paint a border that appears mid-interaction.
  // `transition-colors` so the hover wash and the reordering state below arrive as a change rather
  // than a flicker. Colour only — nothing here transitions a size or a position, so a drag stays
  // exactly as responsive as the pointer.
  //
  // Two drop markers, not one. `dropIndexFor` returns an *insertion* index in `[0, n]`, so the
  // rightmost drop has no column to mark the front of — its own comment says as much — and that
  // position used to render no indicator at all while still being a valid drop. `-end` marks the
  // last column's trailing edge instead.
  'bg-surface-header text-caption text-foreground px-inline align-middle font-medium relative transition-colors duration-fast data-[drop-target]:shadow-[inset_2px_0_0_var(--color-primary)] data-[drop-target-end]:shadow-[inset_-2px_0_0_var(--color-primary)]',
  {
    variants: {
      // A header reads as a label for the column beneath it, so it follows the cells' own
      // alignment rather than staying left while a numeric column's values sit right.
      align: {
        left: 'text-left',
        center: 'text-center',
        right: 'text-right',
      },
      density: {
        compact: 'h-control-sm',
        cozy: 'h-control',
        comfortable: 'h-control-lg',
      },
      // `border-r` is not in this string, deliberately — it moves to a compound variant below,
      // gated on `pinned: false`. A pinned cell's trailing edge is drawn by a `box-shadow` instead
      // (see the compounds below), and baking a real `border-r` in here unconditionally would
      // apply to pinned cells too: `last:border-r-0` cancels it for the actions column (the actual
      // last DOM child, via a `:last-child`-qualified selector that reliably outranks a plain
      // `.border-r` on specificity), but nothing cancels it for the select column, which isn't
      // last — so it alone would keep stacking a real `border-r` underneath its own box-shadow,
      // reading as one visibly heavier line than every other divider, including the actions
      // column's own pinned edge right next to it.
      // Three values, most rules to none. `rows` carries no `last:border-r-0` because that class
      // exists only to cancel the `border-r` compound below, and that compound is gated on
      // `variant: 'grid'` — so it never reaches this value. The pinned cells' inset shadows are
      // gated the same way, deliberately: they are how a sticky cell draws a trailing edge that
      // `border-collapse` refuses to paint, so extending them here would give `rows` back the one
      // vertical rule it exists to remove. `list` has always answered this the same way.
      variant: {
        grid: 'border-input border-b last:border-r-0',
        rows: 'border-input border-b',
        list: '',
      },
      // The sort toggle button disables its own ghost-hover (`hover:bg-transparent` in
      // data-grid-header-cell.tsx) precisely so the header cell itself can own the hover wash
      // instead — without this, that button's hover suppression had nothing to hand off to.
      sortable: {
        true: 'hover:bg-surface-header-hover cursor-pointer',
        false: '',
      },
      // The column being held. `usePointerDrag` already returns `isDragging`, which nothing
      // consumed — so a reorder showed where the column would land and never showed which column
      // was moving. The wash is the same one hover uses, because it means the same thing: this
      // cell is the one under the gesture.
      dragging: {
        true: 'bg-surface-header-hover',
        false: '',
      },
      // The header row's own `bg-surface-header` (above) is already opaque, so a pinned header cell
      // needs no separate background — only `sticky` (the offset itself is a per-column pixel
      // value, so it's an inline style, computed once in `core/pinned-offset.ts` and shared with
      // the body cell) and `z-sticky` so it paints above unpinned cells scrolling underneath it.
      pinned: {
        start: 'sticky z-sticky',
        end: 'sticky z-sticky',
        false: '',
      },
    },
    compoundVariants: [
      // The real `border-r` every unpinned cell divider uses — see the comment on `variant` above
      // for why this is a compound, not part of the base string.
      { pinned: false, variant: 'grid', class: 'border-r' },
      // `border-collapse` (`tableVariants`) does not reliably paint a cell's own `border-r`/
      // `border-l` once that cell is `position: sticky` — confirmed empirically: the collapsed-
      // borders model resolves adjacent-cell borders against the cell's *layout* position in the
      // table grid, not its scrolled/stuck one, so the separator silently fails to render at the
      // exact edge that most needs one. `box-shadow` isn't part of that model at all, so an inset
      // shadow at the pinned cell's own trailing edge paints correctly regardless — using the same
      // `--color-input` custom property `border-input` already resolves to, so it's the same
      // color as every other cell divider, just a different CSS mechanism. Only in the 'grid'
      // variant, matching every other cell border's own on/off switch.
      { pinned: 'start', variant: 'grid', class: 'shadow-[inset_-1px_0_0_var(--color-input)]' },
      { pinned: 'end', variant: 'grid', class: 'shadow-[inset_1px_0_0_var(--color-input)]' },
    ],
    defaultVariants: {
      align: 'left',
      density: 'comfortable',
      variant: 'grid',
      sortable: false,
      pinned: false,
      dragging: false,
    },
  },
);

// A table cell's own `height` is a floor, not a cap — content taller than it grows the row rather
// than clipping, so a wrapped or clamped cell in one column is what sets the row's actual height;
// this and `cellContentVariants` below don't need to coordinate on that.
// A band reads as one strip: its own ground, and no cell borders inside it to cut it into columns.
export const groupRowVariants = cva('bg-muted border-input border-b');

export const cellVariants = cva('px-inline align-middle', {
  variants: {
    // Set on the cell rather than the inner content box: the inner box is `display: block` and
    // fills the cell, so aligning it there would move nothing.
    align: {
      left: 'text-left',
      center: 'text-center',
      right: 'text-right',
    },
    density: {
      compact: 'h-control-sm text-caption',
      cozy: 'h-control text-body',
      comfortable: 'h-control-lg text-body',
    },
    // `border-r` is not in this string — see the identical comment on `headerCellVariants`'s own
    // `variant` above. It moves to a compound below, gated on `pinned: false`, or the select
    // column (pin-start, never `:last-child`) would stack a real `border-r` under its own
    // box-shadow separator and read as a heavier line than the actions column's (pin-end,
    // already zeroed via `last:border-r-0`).
    // See the identical map on `headerCellVariants` for why `rows` omits `last:border-r-0` and why
    // it is left out of every `variant: 'grid'` compound below.
    variant: {
      grid: 'border-input border-b last:border-r-0',
      rows: 'border-input border-b',
      list: '',
    },
    // Unlike the header row, a body `<td>` has no background of its own by default — it shows
    // whatever `rowVariants` painted on the `<tr>` through it. A sticky cell needs its own opaque
    // background (the row's own background does not reliably keep painting behind a stickily
    // repositioned descendant across browsers), so a pinned cell gets one explicitly here instead
    // of relying on the row underneath it.
    pinned: {
      start: 'bg-card sticky z-sticky',
      end: 'bg-card sticky z-sticky',
      false: '',
    },
    // Only meaningful paired with `pinned` (see compoundVariants) — an unpinned selected cell
    // already gets its background from the row via `rowVariants`, same as every other cell, and
    // so does an unpinned hovered one.
    selected: {
      true: '',
      false: '',
    },
  },
  compoundVariants: [
    // Matches `rowVariants`'s own `selected: true` background so a pinned cell in a selected row
    // doesn't read as unselected once it's lifted onto its own sticky background above.
    { pinned: 'start', selected: true, class: 'bg-primary-subtle' },
    { pinned: 'end', selected: true, class: 'bg-primary-subtle' },
    // The same argument for hover, which the selected case above missed. The opaque background a
    // pinned cell needs is exactly what stops the row's `hover:bg-accent` showing through it, so
    // the cell repeats the hover itself — otherwise pointing at a row lights up its middle while
    // the pinned columns at both edges stay behind, and the row reads as three separate strips.
    // Gated on `selected: false` to match the row, which also drops its hover once selected.
    { pinned: 'start', selected: false, class: 'group-hover:bg-accent' },
    { pinned: 'end', selected: false, class: 'group-hover:bg-accent' },
    // The real `border-r` every unpinned cell divider uses — see the comment on `variant` above.
    { pinned: false, variant: 'grid', class: 'border-r' },
    // See the identical compound on `headerCellVariants` — `border-collapse` does not reliably
    // paint a `border-r`/`border-l` on a `position: sticky` cell, so the pinned edge gets an inset
    // `box-shadow` instead, in the same `--color-input` color every other cell border uses. Only
    // in the 'grid' variant, matching every other cell border's own on/off switch.
    { pinned: 'start', variant: 'grid', class: 'shadow-[inset_-1px_0_0_var(--color-input)]' },
    { pinned: 'end', variant: 'grid', class: 'shadow-[inset_1px_0_0_var(--color-input)]' },
  ],
  defaultVariants: {
    align: 'left',
    density: 'comfortable',
    variant: 'grid',
    pinned: false,
    selected: false,
  },
});

// The overflow behaviour lives on an inner element, not `cellVariants` above — `-webkit-line-clamp`
// (an arbitrary line count, so it comes in as a style, not a class) needs a box of its own that
// isn't also `display: table-cell`, which a `<td>` can never stop being without breaking table
// layout.
export const cellContentVariants = cva('', {
  variants: {
    wrap: {
      truncate: 'block overflow-hidden text-ellipsis whitespace-nowrap',
      normal: 'block whitespace-normal',
      // `meta.control`. Neither half of the truncate treatment survives contact with a control:
      // there is no ellipsis to show, and `overflow: hidden` clips a box that a system column
      // sizes to exactly its control plus padding — zero slack, so a fractional root font size or
      // a browser zoom step shaves the control's own 1px border and it reads as blurred on the
      // clipped edge.
      //
      // Flex, not block, because the cell's `text-align` cannot move what is inside: `Checkbox` is
      // `grid place-items-center` and the actions trigger is a button, so both are block-level.
      // Under `text-align` they sit flush against the start edge and only look centred while the
      // column's width happens to match them exactly.
      control: 'flex items-center',
    },
    align: { left: '', center: '', right: '' },
  },
  // Only the control mode aligns here. Text keeps taking its alignment from the cell, where
  // `text-align` reaches the inline content directly.
  compoundVariants: [
    { wrap: 'control', align: 'left', class: 'justify-start' },
    { wrap: 'control', align: 'center', class: 'justify-center' },
    { wrap: 'control', align: 'right', class: 'justify-end' },
  ],
  defaultVariants: { wrap: 'truncate', align: 'left' },
});
